import { useState, useMemo } from 'react';
import {
  Search, Upload, ImagePlus, Eye, Send, Trash2, X, ChevronRight,
  FolderOpen, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmModal } from '../ui/ConfirmModal';
import { MediaUploadModal } from './MediaUploadModal';
import { MediaViewModal } from './MediaViewModal';
import type { MediaConfig, MediaItem, MediaUploadPayload } from './MediaConfig';
import './media-center.css';

interface Props {
  config: MediaConfig;
  midias: MediaItem[];
  isLoading: boolean;
  onCreate: (payload: MediaUploadPayload) => Promise<any>;
  onRemove: (id: string) => Promise<any>;
}

export function MediaCenterCore({
  config,
  midias,
  isLoading,
  onCreate,
  onRemove,
}: Props) {
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewItem, setViewItem] = useState<MediaItem | null>(null);
  const [delTarget, setDelTarget] = useState<MediaItem | null>(null);

  // Mapeamento de categorias para renderização rápida
  const catMap = useMemo(() => {
    return Object.fromEntries(config.categories.map((c) => [c.id, c]));
  }, [config.categories]);

  // Filtros combinados de busca textual e categoria
  const filteredMidias = useMemo(() => {
    return midias.filter((m) => {
      const q = search.trim().toLowerCase();
      if (q && !m.titulo.toLowerCase().includes(q) && !m.descricao?.toLowerCase().includes(q)) {
        return false;
      }
      if (filterCat && m.categoria !== filterCat) {
        return false;
      }
      return true;
    });
  }, [midias, search, filterCat]);

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await onRemove(delTarget.id);
      toast.success('Mídia removida com sucesso.');
    } catch {
      toast.error('Erro ao remover mídia.');
    } finally {
      setDelTarget(null);
    }
  };

  const fmtDate = (d: string) => {
    try {
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return d;
    }
  };

  return (
    <div className="mdc-page animate-fadeIn">
      {/* Hero Banner Institucional */}
      <div className="mdc-hero">
        <div className="mdc-hero-circle c1" />
        <div className="mdc-hero-circle c2" />
        <div className="mdc-hero-content">
          <div>
            <h1 className="mdc-hero-title">
              <FolderOpen size={22} />
              {config.labels.heroTitle}
            </h1>
            <p className="mdc-hero-subtitle">{config.labels.heroSubtitle}</p>
          </div>
          {config.features.canManage !== false && (
            <button
              type="button"
              className="mdc-btn-upload"
              onClick={() => setUploadOpen(true)}
            >
              <Upload size={15} /> Nova Mídia
            </button>
          )}
        </div>
      </div>

      {/* Como funciona banner */}
      {config.features.showHowItWorksBanner && (
        <div className="mdc-how-banner">
          <span className="mdc-how-label">Como funciona?</span>
          <div className="mdc-how-steps">
            <div className="mdc-how-step">
              <span className="mdc-how-num">1</span>
              <span>Crie seu folder no Canva ou com designer</span>
            </div>
            <ChevronRight size={13} style={{ opacity: 0.5, flexShrink: 0 }} />
            <div className="mdc-how-step">
              <span className="mdc-how-num">2</span>
              <span>Faça o upload e categorize</span>
            </div>
            <ChevronRight size={13} style={{ opacity: 0.5, flexShrink: 0 }} />
            <div className="mdc-how-step">
              <span className="mdc-how-num">3</span>
              <span>Dispare para as famílias via comunicados</span>
            </div>
          </div>
        </div>
      )}

      {/* Container Principal com Toolbar e Grid */}
      <div className="mdc-container">
        <div className="mdc-toolbar">
          <div className="mdc-toolbar-left">
            <div className="mdc-search-box">
              <Search size={14} className="mdc-search-icon" />
              <input
                type="text"
                className="mdc-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={config.labels.searchPlaceholder || 'Buscar no acervo de mídias...'}
              />
              {search && (
                <button
                  type="button"
                  className="mdc-search-clear"
                  onClick={() => setSearch('')}
                  title="Limpar busca"
                >
                  <X size={13} />
                </button>
              )}
            </div>

            {config.features.canManage !== false && (
              <button
                type="button"
                className="mdc-btn-upload"
                onClick={() => setUploadOpen(true)}
              >
                <Upload size={14} /> Nova Mídia
              </button>
            )}
          </div>

          <div className="mdc-cats">
            <button
              type="button"
              className={`mdc-cat-pill ${!filterCat ? 'active' : ''}`}
              onClick={() => setFilterCat('')}
            >
              Todas ({midias.length})
            </button>
            {config.categories.map((c) => {
              const count = midias.filter((m) => m.categoria === c.id).length;
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`mdc-cat-pill ${filterCat === c.id ? 'active' : ''}`}
                  onClick={() => setFilterCat(filterCat === c.id ? '' : c.id)}
                  style={
                    filterCat === c.id
                      ? { background: c.color, color: 'white', borderColor: c.color }
                      : undefined
                  }
                >
                  <c.icon size={12} />
                  {c.label} {count > 0 && `(${count})`}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mdc-scroll-area">
          {isLoading ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
              Carregando acervo de mídias...
            </div>
          ) : filteredMidias.length === 0 ? (
            <div className="mdc-empty">
              <div className="mdc-empty-circle">
                <ImagePlus size={36} />
              </div>
              <h3 className="mdc-empty-title">
                {search || filterCat ? 'Nenhuma mídia encontrada' : (config.labels.emptyTitle || 'Nenhuma mídia cadastrada ainda')}
              </h3>
              <p className="mdc-empty-desc">
                {search || filterCat
                  ? 'Nenhum folder corresponde aos termos ou filtros selecionados. Tente ajustar os termos.'
                  : (config.labels.emptySubtitle || 'Faça o upload de folders institucionais, informativos e campanhas para enviar às famílias.')}
              </p>
              {search || filterCat ? (
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setSearch('');
                    setFilterCat('');
                  }}
                >
                  Limpar Filtros
                </button>
              ) : config.features.canManage !== false ? (
                <button
                  type="button"
                  className="mdc-btn-upload"
                  onClick={() => setUploadOpen(true)}
                >
                  <Upload size={14} /> Fazer Primeiro Upload
                </button>
              ) : null}
            </div>
          ) : (
            <div className="mdc-grid">
              {filteredMidias.map((m) => {
                const cat = catMap[m.categoria] || { id: 'OUTRO', label: 'Outros', color: '#6b7280' };
                return (
                  <div key={m.id} className="mdc-card">
                    <div className="mdc-card-img-wrap" onClick={() => setViewItem(m)}>
                      <img src={m.arquivoUrl} alt={m.titulo} loading="lazy" />
                      <div className="mdc-card-overlay">
                        <Eye size={20} />
                      </div>
                    </div>

                    <div className="mdc-card-body">
                      <div
                        className="mdc-card-badge"
                        style={{ background: `${cat.color}15`, color: cat.color }}
                      >
                        {cat.label}
                      </div>
                      <h4 className="mdc-card-title" title={m.titulo}>
                        {m.titulo}
                      </h4>
                      <span className="mdc-card-date">{fmtDate(m.criadoEm)}</span>
                    </div>

                    <div className="mdc-card-actions">
                      {config.onSendToComunicado ? (
                        <button
                          type="button"
                          className="mdc-action-btn send"
                          title="Criar comunicado com este folder"
                          onClick={() => config.onSendToComunicado!(m)}
                        >
                          <Send size={14} />
                        </button>
                      ) : <div />}

                      {config.features.canManage !== false && (
                        <button
                          type="button"
                          className="mdc-action-btn del"
                          title="Excluir mídia"
                          onClick={() => setDelTarget(m)}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Upload */}
      <MediaUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSubmit={onCreate}
        categories={config.categories}
        maxFileSizeMb={config.features.maxFileSizeMb}
      />

      {/* Modal de Visualização */}
      <MediaViewModal
        media={viewItem}
        onClose={() => setViewItem(null)}
        categories={config.categories}
        onSendToComunicado={config.onSendToComunicado}
      />

      {/* Confirmação de Exclusão */}
      <ConfirmModal
        open={!!delTarget}
        title="Remover Mídia do Banco"
        message={delTarget ? `Tem certeza que deseja remover "${delTarget.titulo}"? Esta ação desativará o arquivo do acervo.` : ''}
        confirmLabel="Sim, remover"
        cancelLabel="Cancelar"
        variant="danger"
        onCancel={() => setDelTarget(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}
